// src/pages/ArbitragePage.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../components/AuthGate";
import PremiumUpsellBanner from "../components/PremiumUpsellBanner";
import ArbitrageSection from "../components/ArbitrageSection";
import { api } from "../api";
import "../styles/ArbitragePage.css";

const ARB_REFRESH_DEBUG = process.env.NODE_ENV !== "production";

const buildArbFingerprint = (payload = {}) =>
  JSON.stringify(
    (payload?.fixtures_with_arbs || []).map((fx) => ({
      fixture_id: fx?.fixture_id,
      best_profit_pct: fx?.best_profit_pct,
      opportunities: (fx?.opportunities || []).map((arb) => ({
        market: arb?.market,
        type: arb?.type,
        profit_pct: arb?.profit_pct,
        leg1: [arb?.leg1?.bookmaker, arb?.leg1?.odds, arb?.leg1?.selection],
        leg2: [arb?.leg2?.bookmaker, arb?.leg2?.odds, arb?.leg2?.selection],
        leg3: [arb?.leg3?.bookmaker, arb?.leg3?.odds, arb?.leg3?.selection],
      })),
    }))
  );

const ArbitragePage = () => {
  const { isPremium, isAdmin, loading: authLoading } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFixture, setExpandedFixture] = useState(null);
  const [pollStatus, setPollStatus] = useState(null); // { ok, msg } | null
  const [refreshNotice, setRefreshNotice] = useState("");
  const [quickCalcForm, setQuickCalcForm] = useState({
    side_odds: "",
    current_opposite_odds: "",
    target_profit_pct: 0,
  });
  const [quickCalcStake, setQuickCalcStake] = useState(100);
  const [quickCalcLoading, setQuickCalcLoading] = useState(false);
  const [quickCalcResult, setQuickCalcResult] = useState(null);
  const [quickCalcError, setQuickCalcError] = useState("");
  const lastArbFingerprintRef = useRef("");

  const fetchArbitrage = useCallback(async ({ refreshOdds = false, source = "auto" } = {}) => {
    if (!isPremium) {
      setLoading(false);
      return;
    }
    try {
      if (ARB_REFRESH_DEBUG) {
        console.log("[ArbRefresh] click", { source, refreshOdds });
      }
      setLoading(true);
      setError(null);
      setRefreshNotice("");
      const previousFingerprint = lastArbFingerprintRef.current;

      if (refreshOdds) {
        if (ARB_REFRESH_DEBUG) {
          console.log("[ArbRefresh] request:start", { url: "/ingest/football/live" });
        }
        try {
          await api.post("/ingest/football/live", null, { params: { _ts: Date.now() } });
        } catch (ingestErr) {
          console.error("Live odds refresh failed before arb scan:", ingestErr);
        }
      }

      if (ARB_REFRESH_DEBUG) {
        console.log("[ArbRefresh] request:start", { url: "/football/arbitrage/scan/all" });
      }
      const { data: result } = await api.get("/football/arbitrage/scan/all");
      setData(result);
      const nextFingerprint = buildArbFingerprint(result);
      lastArbFingerprintRef.current = nextFingerprint;
      setRefreshNotice(
        nextFingerprint === previousFingerprint
          ? "No price change detected."
          : "Updated just now."
      );

      if (ARB_REFRESH_DEBUG) {
        const flatLegs = (result?.fixtures_with_arbs || []).flatMap((fx) =>
          (fx?.opportunities || []).flatMap((arb) => [arb?.leg1, arb?.leg2, arb?.leg3].filter(Boolean))
        );
        const betfairCount = flatLegs.filter((leg) =>
          String(leg?.bookmaker || "").toLowerCase().includes("betfair")
        ).length;
        console.log("[ArbRefresh] response:received", {
          fixturesWithArbs: result?.fixtures_with_arbs?.length || 0,
          totalArbs: result?.total_arbs_found || 0,
          betfairLegCount: betfairCount,
          changed: nextFingerprint !== previousFingerprint,
        });
      }
    } catch (err) {
      console.error("Error fetching arbitrage data:", err);
      setError(err.response?.data?.detail || "Failed to load arbitrage data");
    } finally {
      setLoading(false);
    }
  }, [isPremium]);

  useEffect(() => {
    if (!authLoading) {
      fetchArbitrage({ source: "initial" });
    }
  }, [authLoading, fetchArbitrage]);

  const handlePollLive = async () => {
    setPollStatus({ ok: null, msg: "Polling live odds…" });
    try {
      const { data: res } = await api.post("/ingest/football/live");
      setPollStatus({
        ok: true,
        msg: `Live poll done — ${res.ingest?.fixtures_matched || 0} fixtures updated, ${res.ingest?.odds_rows_written || 0} odds rows written.`,
      });
      await fetchArbitrage({ source: "admin-poll" });
    } catch (err) {
      setPollStatus({
        ok: false,
        msg: err.response?.data?.detail || "Live poll failed",
      });
    }
  };

  const formatKickoff = (utcString) => {
    if (!utcString) return "";
    try {
      return new Date(utcString).toLocaleString(navigator.language, {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return utcString;
    }
  };

  const formatPercent = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? `${n.toFixed(2)}%` : "—";
  };

  const formatDecimal = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(2) : "—";
  };

  const formatMoney = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? `£${n.toFixed(2)}` : "—";
  };

  const getQuickCalcStakePlan = () => {
    const totalStake = Number(quickCalcStake);
    const sideOdds = Number(quickCalcForm.side_odds);
    const currentOppositeOdds = Number(quickCalcResult?.current_opposite_odds_decimal);
    const requiredOppositeOdds = Number(quickCalcResult?.required_opposite_odds_decimal);

    const oppositeOdds = Number.isFinite(currentOppositeOdds) && currentOppositeOdds > 1
      ? currentOppositeOdds
      : Number.isFinite(requiredOppositeOdds) && requiredOppositeOdds > 1
        ? requiredOppositeOdds
        : null;

    if (!Number.isFinite(totalStake) || totalStake <= 0 || !Number.isFinite(sideOdds) || sideOdds <= 1 || !oppositeOdds) {
      return null;
    }

    const inverseA = 1 / sideOdds;
    const inverseB = 1 / oppositeOdds;
    const inverseSum = inverseA + inverseB;
    if (!Number.isFinite(inverseSum) || inverseSum <= 0) return null;

    const sideStake = totalStake * (inverseA / inverseSum);
    const oppositeStake = totalStake * (inverseB / inverseSum);
    const guaranteedReturn = sideStake * sideOdds;
    const guaranteedProfit = guaranteedReturn - totalStake;

    return {
      sideStake,
      oppositeStake,
      guaranteedReturn,
      guaranteedProfit,
      roiPct: totalStake > 0 ? (guaranteedProfit / totalStake) * 100 : 0,
      oppositeOdds,
      usingCurrentOdds: Number.isFinite(currentOppositeOdds) && currentOppositeOdds > 1,
    };
  };

  const handleQuickCalcChange = (field) => (event) => {
    const value = event.target.value;
    setQuickCalcForm((prev) => ({ ...prev, [field]: value }));
  };

  const prefillQuickCalcSideOdds = (oddsValue) => {
    setQuickCalcForm((prev) => ({ ...prev, side_odds: String(oddsValue || "") }));
    setQuickCalcError("");
  };

  const runQuickCalc = async (event) => {
    event.preventDefault();
    const sideOdds = String(quickCalcForm.side_odds || "").trim();
    const currentOppositeOdds = String(quickCalcForm.current_opposite_odds || "").trim();
    if (!sideOdds) {
      setQuickCalcError("Enter side odds first.");
      setQuickCalcResult(null);
      return;
    }

    setQuickCalcLoading(true);
    setQuickCalcError("");
    try {
      const params = {
        side_odds: sideOdds,
        target_profit_pct: Number(quickCalcForm.target_profit_pct) || 0,
      };
      if (currentOppositeOdds) params.current_opposite_odds = currentOppositeOdds;
      const { data: result } = await api.get("/football/arbitrage/calc/opposite", { params });
      setQuickCalcResult(result || null);
    } catch (err) {
      setQuickCalcResult(null);
      setQuickCalcError(err?.response?.data?.detail || err?.response?.data?.message || "Could not calculate arbitrage.");
    } finally {
      setQuickCalcLoading(false);
    }
  };

  const quickCalcStakePlan = getQuickCalcStakePlan();

  // Show upsell for non-premium users
  if (!authLoading && !isPremium) {
    return (
      <div className="arb-page">
        <div className="arb-page-header">
          <h1>Arbitrage Finder</h1>
          <p className="arb-page-subtitle">
            Find guaranteed profit opportunities across bookmakers
          </p>
        </div>

        <div className="arb-premium-gate">
          <div className="arb-gate-icon">🔒</div>
          <h2>Premium Feature</h2>
          <p>
            The Arbitrage Finder scans all upcoming fixtures to find opportunities
            where different bookmakers offer odds that guarantee a profit regardless
            of the outcome.
          </p>
          <ul className="arb-gate-features">
            <li>Real-time scanning of all fixtures</li>
            <li>Automatic stake calculator</li>
            <li>Best odds comparison across bookmakers</li>
            <li>Instant notifications when arbs appear</li>
          </ul>
          <PremiumUpsellBanner
            message="Unlock the Arbitrage Finder and all premium features"
            mode="link"
          />
        </div>
      </div>
    );
  }

  if (authLoading || loading) {
    return (
      <div className="arb-page">
        <div className="arb-page-header">
          <h1>Arbitrage Finder</h1>
        </div>
        <div className="arb-loading">
          <div className="arb-loading-spinner" />
          <p>Scanning fixtures for arbitrage opportunities...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="arb-page">
        <div className="arb-page-header">
          <h1>Arbitrage Finder</h1>
        </div>
        <div className="arb-error">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  const { fixtures_scanned, fixtures_with_arbs, total_arbs_found, warning } = data || {};

  return (
    <div className="arb-page">
      <div className="arb-page-header">
        <h1>Arbitrage Finder</h1>
        <p className="arb-page-subtitle">
          Find guaranteed profit opportunities across bookmakers
        </p>
      </div>

      {/* Stats Summary */}
      <div className="arb-stats-row">
        <div className="arb-stat-card">
          <span className="arb-stat-value">{fixtures_scanned || 0}</span>
          <span className="arb-stat-label">Fixtures Scanned</span>
        </div>
        <div className="arb-stat-card highlight">
          <span className="arb-stat-value">{total_arbs_found || 0}</span>
          <span className="arb-stat-label">Arbs Found</span>
        </div>
        <div className="arb-stat-card">
          <span className="arb-stat-value">{fixtures_with_arbs?.length || 0}</span>
          <span className="arb-stat-label">Fixtures with Arbs</span>
        </div>
      </div>

      {/* Warning */}
      {warning && (
        <div className="arb-page-warning">
          <span>⚠️</span> {warning}
        </div>
      )}

      <div className="arb-quick-calc-card">
        <div className="arb-quick-calc-header">
          <h3>Quick Arb Calculator</h3>
          <span>Find required opposite odds instantly</span>
        </div>
        <form className="arb-quick-calc-form" onSubmit={runQuickCalc}>
          <label>
            Side odds
            <input
              type="text"
              value={quickCalcForm.side_odds}
              onChange={handleQuickCalcChange("side_odds")}
              placeholder="e.g. 2.2, 6/5, evens"
            />
          </label>
          <label>
            Current opposite odds (optional)
            <input
              type="text"
              value={quickCalcForm.current_opposite_odds}
              onChange={handleQuickCalcChange("current_opposite_odds")}
              placeholder="e.g. 1.45"
            />
          </label>
          <label>
            Target profit %
            <input
              type="number"
              step="0.01"
              value={quickCalcForm.target_profit_pct}
              onChange={handleQuickCalcChange("target_profit_pct")}
            />
          </label>
          <button
            type="submit"
            className="arb-refresh-btn arb-quick-calc-btn"
            disabled={quickCalcLoading}
          >
            {quickCalcLoading ? "Calculating…" : "Calculate"}
          </button>
        </form>
        {quickCalcError && <p className="arb-quick-calc-error">{quickCalcError}</p>}
        {quickCalcResult && (
          <div className="arb-quick-calc-result">
            <div className="arb-quick-calc-grid">
              <div className="arb-quick-calc-item">
                <span>Required opposite odds</span>
                <strong>{formatDecimal(quickCalcResult.required_opposite_odds_decimal)}</strong>
              </div>
              <div className="arb-quick-calc-item">
                <span>Target profit</span>
                <strong>{formatPercent(quickCalcResult.target_profit_pct)}</strong>
              </div>
            </div>

            <div className="arb-quick-stake-card">
              <div className="arb-quick-stake-header">
                <div>
                  <div className="arb-quick-stake-title">Stake Calculator</div>
                  <div className="arb-quick-stake-subtitle">
                    {quickCalcStakePlan
                      ? `Using ${quickCalcStakePlan.usingCurrentOdds ? "current" : "required"} opposite odds @ ${formatDecimal(quickCalcStakePlan.oppositeOdds)}`
                      : "Enter valid odds to see the stake split"}
                  </div>
                </div>
                <label className="arb-quick-stake-input-label">
                  Total stake
                  <div className="arb-quick-stake-input-wrap">
                    <span className="arb-currency">£</span>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={quickCalcStake}
                      onChange={(event) => setQuickCalcStake(Number(event.target.value) || 0)}
                    />
                  </div>
                </label>
              </div>

              {quickCalcStakePlan ? (
                <>
                  <div className="arb-quick-calc-grid arb-quick-stake-summary">
                    <div className="arb-quick-calc-item">
                      <span>Guaranteed return</span>
                      <strong>{formatMoney(quickCalcStakePlan.guaranteedReturn)}</strong>
                    </div>
                    <div className="arb-quick-calc-item">
                      <span>Guaranteed profit</span>
                      <strong>{formatMoney(quickCalcStakePlan.guaranteedProfit)}</strong>
                    </div>
                    <div className="arb-quick-calc-item">
                      <span>ROI</span>
                      <strong>{formatPercent(quickCalcStakePlan.roiPct)}</strong>
                    </div>
                    <div className="arb-quick-calc-item">
                      <span>Opposite odds used</span>
                      <strong>{formatDecimal(quickCalcStakePlan.oppositeOdds)}</strong>
                    </div>
                  </div>

                  <div className="arb-quick-stakes-breakdown">
                    <div className="arb-quick-stake-row">
                      <span>Side stake @ {formatDecimal(quickCalcForm.side_odds)}</span>
                      <strong>{formatMoney(quickCalcStakePlan.sideStake)}</strong>
                    </div>
                    <div className="arb-quick-stake-row">
                      <span>Opposite stake @ {formatDecimal(quickCalcStakePlan.oppositeOdds)}</span>
                      <strong>{formatMoney(quickCalcStakePlan.oppositeStake)}</strong>
                    </div>
                  </div>
                </>
              ) : null}
            </div>

            {typeof quickCalcResult.current_opposite_odds_decimal === "number" && (
              <div className={`arb-quick-calc-status ${quickCalcResult.is_arb_now ? "is-green" : "is-red"}`}>
                <div>
                  <span>Arb now?</span>
                  <strong>{quickCalcResult.is_arb_now ? "Yes" : "No"}</strong>
                </div>
                <div>
                  <span>Current profit %</span>
                  <strong>{formatPercent(quickCalcResult.profit_pct_now)}</strong>
                </div>
                <div>
                  <span>Arb margin</span>
                  <strong>{formatPercent(quickCalcResult.arb_margin)}</strong>
                </div>
                <div>
                  <span>Meets target?</span>
                  <strong>{quickCalcResult.meets_target ? "Yes" : "No"}</strong>
                </div>
              </div>
            )}

            {quickCalcResult.message ? (
              <p className="arb-refresh-note">{quickCalcResult.message}</p>
            ) : null}
          </div>
        )}
      </div>

      {/* Results */}
      {fixtures_with_arbs && fixtures_with_arbs.length > 0 ? (
        <div className="arb-fixtures-list">
          {fixtures_with_arbs.map((fx) => (
            <div key={fx.fixture_id} className="arb-fixture-card">
              <div
                className="arb-fixture-header"
                onClick={() =>
                  setExpandedFixture(
                    expandedFixture === fx.fixture_id ? null : fx.fixture_id
                  )
                }
              >
                <div className="arb-fixture-info">
                  <div className="arb-fixture-teams">
                    {fx.home_team} vs {fx.away_team}
                  </div>
                  <div className="arb-fixture-meta">
                    {fx.comp} • {formatKickoff(fx.kickoff_utc)}
                  </div>
                </div>
                <div className="arb-fixture-badge">
                  <span className="arb-count">{fx.opportunities.length}</span>
                  <span className="arb-profit">+{fx.best_profit_pct.toFixed(2)}%</span>
                </div>
              </div>

              {expandedFixture === fx.fixture_id && (
                <div className="arb-fixture-details">
                  <ArbitrageSection
                    data={{
                      opportunities: fx.opportunities,
                      best_odds: {},
                      total_markets_scanned: 0,
                      warning: null,
                    }}
                    onSelectLegOdds={prefillQuickCalcSideOdds}
                  />
                  <Link
                    to={`/fixture/${fx.fixture_id}`}
                    className="arb-view-fixture-link"
                  >
                    View Full Fixture →
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="arb-empty-state">
          <div className="arb-empty-icon">📊</div>
          <h3>No Arbitrage Opportunities Found</h3>
          <p>
            We've scanned {fixtures_scanned || 0} upcoming fixtures but haven't found
            any arbitrage opportunities at the moment. Arbs are rare and usually
            short-lived - check back closer to match times when odds tend to diverge more.
          </p>
        </div>
      )}

      {/* Refresh / admin controls */}
      <div className="arb-refresh-section">
        <button
          className="arb-refresh-btn"
          onClick={() => fetchArbitrage({ refreshOdds: true, source: "manual-refresh" })}
        >
          🔄 Refresh Scan
        </button>

        {isAdmin && (
          <button
            className="arb-refresh-btn arb-poll-live-btn"
            onClick={handlePollLive}
            disabled={pollStatus?.msg === "Polling live odds…"}
          >
            📡 Poll Live + Scan Arb
          </button>
        )}

        {pollStatus && (
          <p className={pollStatus.ok === false ? "arb-refresh-note arb-poll-error" : "arb-refresh-note"}>
            {pollStatus.msg}
          </p>
        )}
        {!!refreshNotice && <p className="arb-refresh-note">{refreshNotice}</p>}

        <p className="arb-refresh-note">
          Odds change frequently. Refresh to get the latest opportunities.
        </p>
      </div>
    </div>
  );
};

export default ArbitragePage;
